/**
 * The totals block for an order, on the admin side.
 *
 * The order table listed its line items and then exactly one row — Platform Fee.
 * No subtotal, no GST, no delivery, no loyalty, wallet, bonus or subscription,
 * and no grand total. Somebody looking at an order in the admin could not
 * reconcile it against what the customer was charged, or against the invoice
 * that had already been emailed to them.
 *
 * A PORT OF invoice_totals.py, DELIBERATELY ROW FOR ROW. There is no shared
 * runtime between a Django PDF and a React table, so the choice is one rule
 * written twice or two documents that disagree — and the invoice's own history
 * is what disagreement costs.
 *
 * The important part, and the reason this cannot be a naive list of fields:
 *
 *     Checkout.amount = sellprice + subscription - wallet - bonus
 *
 * with `sellprice` already net of loyalty, the coupon, the subscriber offer and
 * the self-pickup waiver. So `amount` is NOT a subtotal. Printing it as one and
 * subtracting loyalty underneath counts loyalty twice, which is exactly what
 * the invoice did before it was rebuilt.
 *
 * The gross subtotal is reconstructed by adding back what the app took off, and
 * each of those is then printed as its own line. Adding a figure in and taking
 * it out again cannot change the sum, so the block reconciles by construction.
 */

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const money = (value) => num(value).toFixed(2);

/**
 * @returns {{rows: Array<{label, value, kind}>, grossSubtotal, reconciles}}
 *
 * `kind` is 'charge' | 'deduction' | 'total', so the table can style a
 * deduction differently without parsing the minus sign back out of a string.
 */
export function orderTotalsRows(order) {
  if (!order || typeof order !== 'object') {
    return { rows: [], grossSubtotal: 0, reconciles: true };
  }

  const loyalty = num(order.loyalty_points_applied);
  const wallet = num(order.wallet_offer_amount);
  const bonus = num(order.bonus_offer_amount);
  const coupon = num(order.promo_coupon_discount);
  const selfPickup = num(order.self_pickup_offer_amount);
  const randomOffer = num(order.random_offer_amount);
  const subscription = num(order.subscription_amount);
  const platformFee = num(order.platform_fee);
  const tax = num(order.tax);
  const delivery = num(order.delivery_charge);
  const net = num(order.amount);
  const total = num(order.total_amount);

  // Everything the app deducted from the goods, or paid for with credit, put
  // back — and the subscription taken out, because it is added again below as
  // its own line.
  const grossSubtotal = Math.max(
    0,
    net + loyalty + wallet + bonus + coupon + selfPickup + randomOffer - subscription
  );

  const rows = [{ label: 'Sub Total', value: money(grossSubtotal), kind: 'charge' }];

  const deduction = (label, amount) => {
    if (amount > 0) rows.push({ label, value: `-${money(amount)}`, kind: 'deduction' });
  };
  const charge = (label, amount, always = false) => {
    if (always || amount > 0) rows.push({ label, value: money(amount), kind: 'charge' });
  };

  // In the order the cart applies them, so this reads like the bill the
  // customer already saw.
  deduction('Self-Pickup Waiver', selfPickup);
  deduction('Member Offer', randomOffer);
  deduction(
    order.promo_coupon_code ? `Coupon (${order.promo_coupon_code})` : 'Coupon',
    coupon
  );
  deduction('Loyalty Cash', loyalty);

  charge('Total Tax', tax, true);
  charge('Delivery Charge', delivery, true);
  charge('Platform Fee', platformFee);
  charge('Subscription', subscription);

  // Money the customer already held, applied to the bill. Not discounts —
  // which is why they sit below the charges rather than among them.
  deduction('Paid from Shino Wallet', wallet);
  deduction('Bonus Credit', bonus);

  rows.push({ label: 'Total Amount', value: money(total), kind: 'total' });

  return { rows, grossSubtotal, reconciles: reconciles(rows) };
}

/**
 * What the hub gave away on this order, in rupees.
 *
 * A port of `offer_savings` in invoice_totals.py, added when the printed bill
 * grew a "You saved" line — this file is a row-for-row port of that one, and a
 * port that is a function behind is how two documents start disagreeing again.
 *
 * The coupon, the subscriber offer and the self-pickup waiver, and deliberately
 * NOT loyalty, wallet or bonus: those are the customer's own balance being
 * spent, and telling somebody they "saved" their own money is worse than saying
 * nothing at all.
 *
 * Returned rather than pushed as a row, because it is not part of the sum.
 * `reconciles` below walks every row but the last, so a savings line among them
 * would break the one property this block guarantees.
 */
export function offerSavings(order) {
  if (!order || typeof order !== 'object') return 0;
  return (
    num(order.promo_coupon_discount)
    + num(order.random_offer_amount)
    + num(order.self_pickup_offer_amount)
  );
}

/**
 * The subscription bought with this order, as line items.
 *
 * A port of `plan_lines` in bill_ticket.py. Two shapes, both already in
 * `Checkout.subscription_plan`: a LIST of category subscriptions carrying
 * `category_name`, `no_of_days` and `subscription_price`, or a SINGLE
 * membership object carrying `plan_name`, whose price is `subscription_amount`.
 *
 * The membership case is the one that hid. The products loop walks products,
 * and a plan is not one, so a Rs 600 membership showed up only as a
 * "Subscription" row in the totals — on a table whose line items came to Rs 600
 * less than the bill printed underneath them.
 *
 * @returns {Array<{name, detail, amount}>}
 */
export function planLines(order) {
  const raw = order && typeof order === 'object' ? order.subscription_plan : null;
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .filter((plan) => plan && typeof plan === 'object' && plan.category_name)
      .map((plan) => {
        const days = Math.trunc(num(plan.no_of_days));
        const perDay = num(plan.subscription_price);
        return {
          name: String(plan.category_name).trim(),
          detail: days > 0 && perDay > 0 ? `Rs ${money(perDay)} / day` : '',
          days,
          amount: days > 0 && perDay > 0 ? days * perDay : 0,
        };
      });
  }

  if (typeof raw !== 'object') return [];

  const name = String(raw.plan_name || raw.name || raw.category_name || '').trim();
  let amount = num(order.subscription_amount);
  if (!name && amount <= 0) return [];

  const days = Math.trunc(num(raw.no_of_days));
  const perDay = num(raw.subscription_price);
  let detail = 'Membership plan';
  if (days > 0 && perDay > 0) {
    detail = `Rs ${money(perDay)} / day`;
    if (amount <= 0) amount = days * perDay;
  }
  return [{ name: name || 'Subscription', detail, days, amount }];
}

/**
 * Do the printed lines add up to the printed total?
 *
 * Reads the RENDERED strings rather than the inputs, deliberately: what has to
 * add up is what somebody sees, and a rounding choice made while formatting is
 * exactly the kind of thing that breaks it.
 */
export function reconciles(rows, tolerance = 0.01) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const total = num(rows[rows.length - 1].value);
  const running = rows.slice(0, -1).reduce((sum, row) => sum + num(row.value), 0);
  return Math.abs(running - total) <= tolerance;
}

export default orderTotalsRows;
